// GET /v3/devices 清單 hook（00 §7.1 rich include；legacy webapp 已驗證的 include 組合）。
// v1 不分頁：預設 limit 50 已涵蓋一般帳號（next_cursor 留待清單規模需要時再接）。
// 即時（W3/APP-01，16 §5.2）：清單載入後 subscribe 各裝置 room；room 廣播的
// type:'event' 即時更新該裝置卡的 last_event（不等下次 GET /v3/devices）。

import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from '../../i18n';
import { ApiError, apiFetch } from '../../lib/api';
import { signaling } from '../../lib/ws';
import { useRefetchOnForeground } from '../../lib/useRefetchOnForeground';
import { ensureEventRooms, onLiveDeviceStatus, onLiveEvent } from '../events/liveEvents';
import { liveOnlineFromStatus, liveOnlineOf, presenceOf, type DeviceSummary } from './deviceModel';
import { revokedTombstones } from './revokedTombstones';

export const DEVICE_INCLUDE = 'subscription,online,capability,firmware_upgrade';

export interface DeviceListState {
  devices: DeviceSummary[] | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Trailing-edge window for the post-revocation device-list reload. Long enough to fold a
 * bulk Ops grant revocation (one frame per device) into a single request, short enough
 * that a lone revocation still reconciles with the server well within a user's attention
 * span — the optimistic removal has already updated the UI by then.
 */
const REVOKE_RELOAD_DEBOUNCE_MS = 400;

export function useDeviceList(): DeviceListState {
  const [devices, setDevices] = useState<DeviceSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<DeviceSummary[]>('/v3/devices', { query: { include: DEVICE_INCLUDE } });
      if (!alive.current) return;
      // tombstone 過濾:擋 stale GSI 把剛撤銷的裝置蓋回清單(revokedTombstones.ts,APP#123)。
      setDevices(revokedTombstones.filter(Array.isArray(res.data) ? res.data : [], (d) => d.device_id));
    } catch (e) {
      if (!alive.current) return;
      if (e instanceof ApiError) setError(`${e.message}（${e.code}）`);
      else setError(e instanceof Error ? e.message : i18n.t('common.loadFailed'));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void reload();
    return () => {
      alive.current = false;
    };
  }, [reload]);

  // App-only staleness stop-gap: server-side subscription-expiry teardown can change a
  // device's record_mode / subscription without any push on the device-list payload, and this
  // hook otherwise fetches only on mount + explicit reload. Re-pull once on foreground and on
  // WSS reconnect so the list converges when the user is looking again (no poll; see hook).
  useRefetchOnForeground(reload);

  // subscribe response 補上「device 已在線、client 才取得 owner relation 並晚訂閱」
  // 的必然競態。後續變化仍走下方 ONLINE/OFFLINE presence event，無輪詢。
  useEffect(() => {
    return onLiveDeviceStatus(({ deviceId, status }) => {
      if (!alive.current) return;
      const nowUs = Date.now() * 1_000;
      setDevices((prev) => {
        if (!prev) return prev;
        let changed = false;
        const next = prev.map((d) => {
          if (d.device_id !== deviceId) return d;
          const online = liveOnlineFromStatus(d, status, nowUs);
          const standby = status === 'dormant' || status === 'deep_sleep' || status === 'active' ? status : undefined;
          if (!online && (!standby || d.standby_state === standby)) return d;
          changed = true;
          return { ...d, ...(online ? { online } : {}), ...(standby ? { standby_state: standby } : {}) };
        });
        return changed ? next : prev;
      });
    });
  }, []);

  // 清單就緒 → 訂閱各裝置 room（liveEvents registry 維持訂閱/重連重訂；
  // ws.ts subscribe 冪等，與 events 頁/live 頁共 room 不重複上 wire）。
  useEffect(() => {
    if (devices && devices.length > 0) ensureEventRooms(devices.map((d) => d.device_id));
  }, [devices]);

  // room 廣播即時事件 → 雙更新（16 §4.2 + W3/APP-01）：
  //   1) online 狀態：presence 推播（ONLINE/OFFLINE）直接翻 state；其餘任何
  //      事件＝生命跡象 → 視為 online 並解除 stale_hint（liveOnlineOf）。
  //   2) last_event：僅一般偵測事件、僅在較新時覆寫——presence frame 不入
  //      last_event（它不是 §4.1 的「最近事件」，混入會蓋掉人形/移動標籤）。
  useEffect(() => {
    return onLiveEvent(({ deviceId, ev }) => {
      if (!alive.current) return;
      setDevices((prev) => {
        if (!prev) return prev;
        let changed = false;
        const isPresence = presenceOf(ev.event_id) !== null;
        const next = prev.map((d) => {
          if (d.device_id !== deviceId) return d;
          const live = liveOnlineOf(d, ev.event_id, ev.event_ts);
          const bumpLast = !isPresence && (d.last_event?.event_ts ?? 0) < ev.event_ts;
          if (!live && !bumpLast) return d;
          changed = true;
          return {
            ...d,
            ...(live ? { online: live } : {}),
            ...(bumpLast
              ? { last_event: { event_id: ev.event_id, event_type: ev.event_type, event_ts: ev.event_ts } }
              : {}),
          };
        });
        return changed ? next : prev;
      });
    });
  }, []);

  // Device-scoped revocation (authz_revoked / device_disabled / device_unbound): a
  // device's access was pulled. Spec 18 §3.3/§3.4: drop ONLY that device, keep the
  // session. Optimistic removal is for snappy UI only — it does NOT protect against
  // the reload race: GET /v3/devices reads an eventually-consistent GSI, so the reload
  // fired right after this frame can still contain the device and would overwrite the
  // removal. The tombstone (filtered inside reload) is what actually prevents the
  // resurrection (APP#123). The reload alone suffices when device_id is absent
  // (common/02 §6.9's frame may omit it) — no tombstone possible in that case.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = signaling.on('device_revoked', (info) => {
      if (!alive.current) return;
      if (info.deviceId) {
        revokedTombstones.add(info.deviceId);
        setDevices((prev) => (prev ? prev.filter((d) => d.device_id !== info.deviceId) : prev));
      }
      // Coalesce the authoritative reload. Revocations arrive in BURSTS: restful's Ops
      // grant removal publishes one rev:authz PER DEVICE (RemoveGrantRows), so pulling a
      // grant that covers N devices delivers N frames back to back — one GET /v3/devices
      // each would be a self-inflicted request storm. Safe to defer because the removal
      // above is per-frame and immediate: every frame carries its own device_id, drops
      // that tile and tombstones it, so the UI is never stale while we wait. The reload
      // is only the authoritative catch-up, and it needs to run once, after the burst.
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void reload();
      }, REVOKE_RELOAD_DEBOUNCE_MS);
    });
    return () => {
      if (timer) clearTimeout(timer);
      off();
    };
  }, [reload]);

  return { devices, loading, error, reload };
}
